import { QueryKey, useInfiniteQuery, useQuery, UseQueryOptions } from 'react-query';
import { layoutSettings, sectionSettings } from '../../../config';
import {
  getAllAvailablePages,
  getPageByIdOrSlug,
  getRootMenu,
  getRootMenuItemsBySlug,
  getSectionItems,
  SectionItemsOptions,
} from './api';
import { layoutKeys } from './keys';
import { Layout } from './types';

export function rootMenuQuery<T = Layout.SectionItem[]>(
  options?: UseQueryOptions<Layout.SectionItem[], unknown, T, QueryKey>,
) {
  return {
    queryKey: layoutKeys.rootMenu(''),
    queryFn: () => getRootMenu(),
    keepPreviousData: true,
    ...options,
  };
}

export function useRootMenu<T = Layout.SectionItem[]>(
  options?: UseQueryOptions<Layout.SectionItem[], unknown, T, QueryKey>,
) {
  return useQuery(rootMenuQuery(options));
}

export function usePages<T = Layout.PageViewModelBase[]>(
  options?: UseQueryOptions<Layout.PageViewModelBase[], unknown, T, QueryKey>,
) {
  return useQuery({
    queryKey: layoutKeys.availablePages(),
    queryFn: () => getAllAvailablePages(),
    staleTime: 0,
    cacheTime: layoutSettings.cacheTime,
    ...options,
  });
}

export function usePage<T = Layout.PageView>(
  idOrSlug: string,
  options?: UseQueryOptions<Layout.PageView, Error, T, QueryKey>,
) {
  return useQuery({
    queryKey: layoutKeys.page(idOrSlug),
    queryFn: () => getPageByIdOrSlug(idOrSlug),
    staleTime: 0,
    cacheTime: layoutSettings.cacheTime,
    ...options,
  });
}

export function useMenu<T = Layout.PageView | Layout.SectionItem[]>(
  type: Layout.SectionItemType,
  slug: string,
  options?: UseQueryOptions<Layout.PageView | Layout.SectionItem[], unknown, T, QueryKey>,
) {
  return useQuery({
    queryKey: layoutKeys.pageMenuItems(type, slug),
    queryFn: () => getRootMenuItemsBySlug(type, slug),
    staleTime: 0,
    cacheTime: layoutSettings.cacheTime,
    ...options,
  });
}

export function useSectionItems(options: SectionItemsOptions) {
  return useQuery({
    queryKey: layoutKeys.sectionItems(options),
    queryFn: () => getSectionItems(options),
    staleTime: sectionSettings.staleTime,
    cacheTime: sectionSettings.cacheTime,
    retry: 1,
  });
}

type InfiniteSectionItemsOptions = SectionItemsOptions & {
  initialData: Layout.SectionItem[];
};

export function useInfiniteSectionItems({ initialData, ...options }: InfiniteSectionItemsOptions) {
  return useInfiniteQuery<Layout.SectionItem[]>({
    queryKey: layoutKeys.sectionItems(options),
    queryFn: ({ pageParam }) =>
      getSectionItems({
        ...options,
        skip: pageParam,
        take: options.take,
      }),
    getNextPageParam: (_, allPages) => {
      return allPages.flat().length;
    },
    initialData: {
      pageParams: [initialData.length],
      pages: [initialData],
    },
    enabled: true,
    staleTime: sectionSettings.staleTime,
    cacheTime: sectionSettings.cacheTime,
    keepPreviousData: true,
    retry: 1,
  });
}
